<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAdmin } from 'dashboard/composables/useAdmin';
import Button from 'dashboard/components-next/button/Button.vue';
import TaskAttributeFilterRow from 'kanban/components/filters/TaskAttributeFilterRow.vue';
import {
  MAX_ATTRIBUTE_FILTERS,
  OPERATORS_BY_TYPE,
  isValidCondition,
} from 'kanban/helper/taskFilters';

// Lista de condicoes de atributo (combinadas com E). Edita uma copia e so aplica com tudo valido.
const props = defineProps({
  definitions: { type: Array, required: true },
});

const emit = defineEmits(['close']);

const applied = defineModel({ type: Array, required: true });

const { t } = useI18n();
const route = useRoute();
const { isAdmin } = useAdmin();

const conditions = ref(
  applied.value.map(condition => ({
    ...condition,
    values: [...condition.values],
  }))
);

const usedKeys = computed(() =>
  conditions.value.map(condition => condition.attribute_key)
);
const freeDefinitions = computed(() =>
  props.definitions.filter(
    definition => !usedKeys.value.includes(definition.attribute_key)
  )
);
const canAdd = computed(
  () =>
    conditions.value.length < MAX_ATTRIBUTE_FILTERS &&
    freeDefinitions.value.length > 0
);
const canApply = computed(() =>
  conditions.value.every(condition =>
    isValidCondition(condition, props.definitions)
  )
);

const addCondition = () => {
  const [definition] = freeDefinitions.value;
  const type = definition.attribute_display_type;
  const [operator] = OPERATORS_BY_TYPE[type];
  conditions.value.push({
    attribute_key: definition.attribute_key,
    filter_operator: operator,
    values: type === 'checkbox' ? ['true'] : [],
  });
};

const removeCondition = index => {
  conditions.value.splice(index, 1);
};

const apply = () => {
  applied.value = conditions.value;
  emit('close');
};

const clear = () => {
  conditions.value = [];
  applied.value = [];
  emit('close');
};
</script>

<template>
  <div
    class="flex flex-col gap-3 p-4 border rounded-xl shadow-lg w-[min(42rem,calc(100vw-2rem))] bg-n-alpha-3 backdrop-blur-[100px] border-n-weak"
  >
    <h3 class="text-sm font-medium text-n-slate-12">
      {{ t('KANBAN.FILTERS.ATTRIBUTES.TITLE') }}
    </h3>
    <p v-if="!definitions.length" class="text-sm text-n-slate-11">
      {{ t('KANBAN.CUSTOM_ATTRIBUTES.NO_RECORDS_FOUND') }}
      <router-link
        v-if="isAdmin"
        :to="{
          name: 'attributes_list',
          params: { accountId: route.params.accountId },
        }"
        class="text-n-blue-11 hover:underline"
      >
        {{ t('KANBAN.CUSTOM_ATTRIBUTES.CREATE_LINK') }}
      </router-link>
    </p>
    <template v-else>
      <ul class="flex flex-col gap-2">
        <TaskAttributeFilterRow
          v-for="(condition, index) in conditions"
          :key="`${index}-${condition.attribute_key}`"
          v-model="conditions[index]"
          :definitions="definitions"
          :used-keys="usedKeys"
          @remove="removeCondition(index)"
        />
      </ul>
      <div class="flex flex-wrap items-center gap-2">
        <Button
          v-if="canAdd"
          :label="t('KANBAN.FILTERS.ATTRIBUTES.ADD')"
          icon="i-lucide-plus"
          variant="link"
          size="sm"
          @click="addCondition"
        />
        <div class="flex items-center gap-2 ms-auto">
          <Button
            :label="t('KANBAN.FILTERS.ATTRIBUTES.CLEAR')"
            variant="faded"
            color="slate"
            size="sm"
            @click="clear"
          />
          <Button
            :label="t('KANBAN.FILTERS.ATTRIBUTES.APPLY')"
            :disabled="!canApply"
            size="sm"
            @click="apply"
          />
        </div>
      </div>
    </template>
  </div>
</template>
